import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { html, css, LitElement, unsafeCSS } from 'lit';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { cache } from 'lit/directives/cache.js';
import { when } from 'lit/directives/when.js';
import { choose } from 'lit/directives/choose.js';
import './feedback-list.js';
import './feedback-chat.js';
import '../katapult-elements/katapult-icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import lightTheme from '../../themes/light.styles.js';
import { createConversation } from '../feedback/Feedback.js';
import { capitalCase } from 'change-case';
import { ShouldUpdate } from '../../modules/ShouldUpdate.js';
import { KatapultScrollbars } from '../style-modules/katapult-scrollbars.js';
import { KatapultModel } from '../../modules/katapult-model/KatapultModel.js';

/** @import { CollectionReference, Query } from '@firebase/firestore-types' */

/**
 * @typedef {'JOB'|'NODE'} EntityType
 */

/**
 * @typedef {'ACTION'|'ATTRIBUTE_EVENT'|''} SubEntityType
 */

/**
 * @typedef {Object} FeedbackFilters
 * @property {string} [jobId]
 * @property {string} [projectId]
 * @property {EntityType} [entityType]
 * @property {string} [entityId]
 * @property {SubEntityType} [subEntityType]
 * @property {string} [subEntityId]
 */

class FeedbackElement extends LitElement {
  static properties = {
    inbox: { type: Boolean },
    issuesOnly: { type: Boolean },
    filters: { type: Object },
    conversationRefPath: { type: String },
    allowPrivateFeedback: { type: String },

    _currentUserUid: { type: String },
    _currentUserActiveCompany: { type: String },
    _currentUserRootCompany: { type: String },
    _userFeedbackScope: { type: String },
    _allowPublicFeedback: { type: Boolean },

    nodes: { type: Object },
    mapStyles: { type: Object }
  };

  static styles = [
    unsafeCSS(KatapultScrollbars),
    lightTheme,
    css`
      :host {
        position: relative;
        display: flex;
        flex-direction: column;
        gap: var(--sl-spacing-x-small);
      }
      .title {
        display: flex;
        align-items: center;
        gap: var(--sl-spacing-x-small);
        font-size: var(--sl-font-size-medium);
        font-weight: var(--sl-font-weight-semibold);
        color: var(--sl-color-neutral-800);
      }
      feedback-list {
        overflow-y: auto;
      }
    `
  ];

  render() {
    const { conversationRefPath, inbox, _userFeedbackScope } = this;
    return html`
      ${cache(
        when(
          this.conversationRefPath,
          () => html`
            <feedback-chat
              .userGroup=${this._currentUserActiveCompany}
              .conversationRefPath=${conversationRefPath}
              .mapStyles=${this.mapStyles}
              .nodes=${this.nodes}
              showBackButton
              @close=${() => this.showList()}
            ></feedback-chat>
          `,
          () => html`
            ${when(
              inbox == false,
              () => html`
                <!-- Only show the create conversation button if the user is allowed to create a conversation -->
                ${when(
                  this.allowPrivateFeedback || this._allowPublicFeedback,
                  () => html`
                    <div style="display: flex; justify-content: center; align-items: center;">
                      <katapult-button
                        .callback=${this.#handleCreateConversationClicked.bind(this)}
                        icon="add_comment"
                        style="margin-bottom: var(--sl-spacing-small);"
                        color="var(--secondary-color)"
                        >Start Conversation on ${capitalCase(this.filters.entityType ?? 'item')}</katapult-button
                      >
                    </div>
                  `
                )}
              `
            )}
            <div style="display: flex; align-items: center; min-height: 30px;">
              <sl-switch
                size="small"
                style="margin-left: var(--sl-spacing-3x-small);"
                @sl-change=${(event) => (this.issuesOnly = event.target.checked)}
                >Issues only</sl-switch
              >
              ${when(
                inbox,
                () => html`
                  <sl-dropdown style="margin-left: auto;" hoist @sl-select=${this.#userFeedbackScopeMenuItemSelected.bind(this)}>
                    <sl-button slot="trigger" size="small" caret>
                      ${choose(_userFeedbackScope, [
                        ['inbox', () => 'Inbox'],
                        ['awaiting_reply', () => 'Awaiting Reply'],
                        ['all_conversations', () => 'All Conversations']
                      ])}
                    </sl-button>
                    <sl-menu>
                      <sl-menu-item name="inbox">Inbox</sl-menu-item>
                      <sl-menu-item name="awaiting_reply">Awaiting Reply</sl-menu-item>
                      <sl-menu-item name="all_conversations">All Conversations</sl-menu-item>
                    </sl-menu>
                  </sl-dropdown>
                `
              )}
            </div>
            <feedback-list
              .companyId=${this._currentUserActiveCompany}
              .userId=${this.inbox ? this._currentUserUid : null}
              .jobId=${this.filters?.jobId}
              .entityId=${this.filters?.entityId}
              .entityType=${this.filters?.entityType}
              .subEntityId=${this.filters?.subEntityId}
              .subEntityType=${this.filters?.subEntityType}
              .issuesOnly=${this.issuesOnly}
              .userFeedbackScope=${this._userFeedbackScope}
              .inbox=${this.inbox}
              .allowPrivateFeedback=${this.allowPrivateFeedback}
              .allowPublicFeedback=${this._allowPublicFeedback}
              @conversation-selected=${(e) => this.#conversationSelected(e)}
            ></feedback-list>
          `
        )
      )}
    `;
  }

  constructor() {
    super();

    /** @type {?string} */
    this._currentUserActiveCompany = null;
    /** @type {?string} */
    this._currentUserRootCompany = null;
    /** @type {?string} */
    this._currentUserUid = null;
    /**
     * Whether to display the element as a user's feedback inbox.
     * @type {Boolean}
     */
    this.inbox = false;
    /**
     * Whether to display only issues in the feedback list.
     * @type {Boolean}
     */
    this.issuesOnly = false;
    /**
     * A map of filters to apply to the feedback list.
     * @type {FeedbackFilters}
     */
    this.filters = {};
    /**
     * Whether to allow the current user to view and create private feedback conversations.
     * @type {boolean}
     */
    this.allowPrivateFeedback = false;
    /** @type {?string} */
    this.conversationRefPath = null;
    /** @type {?string} */
    this._defaultFeedbackScope = 'inbox';
    /** @type {string} */
    this._userFeedbackScope = this._defaultFeedbackScope;
    /** @type {boolean} */
    this._allowPublicFeedback = false;
    /** @type {Object} */
    this.nodes = {};
    /** @type {Object} */
    this.mapStyles = {};

    onIdTokenChanged(getAuth(), async (user) => {
      if (user == null) {
        this._currentUserUid = null;
        this._currentUserActiveCompany = null;
        return;
      }
      const { claims } = await user?.getIdTokenResult();
      const { userGroup, rootCompany } = claims ?? {};
      this._currentUserUid = user.uid;
      this._currentUserActiveCompany = /** @type {string} */ (userGroup);
      this._currentUserRootCompany = /** @type {string} */ (rootCompany);
    });
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('filters')) {
      this.#updatePublicFeedbackOptionListener();
    }

    return ShouldUpdate(this, changedProperties);
  }

  #updatePublicFeedbackOptionListener() {
    this.removePublicFeedbackOptionListener?.();

    // Only establish the listener if a job creator ("projectId") is set; this will never be the case for a user inbox
    const jobCreator = this.filters?.projectId;
    if (!jobCreator) return;

    const unsubscribeMapConfig = KatapultModel.fromId(jobCreator).onConfigMapSnapshot((snapshot) => {
      this._allowPublicFeedback = snapshot.val()?.allow_public_feedback === true;
    });

    this.removePublicFeedbackOptionListener = () => {
      unsubscribeMapConfig();
      this._allowPublicFeedback = false;
      this.removePublicFeedbackOptionListener = null;
    };
  }

  #conversationSelected(e) {
    const { conversationRefPath } = e.detail;
    Object.assign(this, { conversationRefPath });
  }

  #userFeedbackScopeMenuItemSelected(e) {
    const { item } = e.detail;
    this._userFeedbackScope = item?.getAttribute('name') ?? this._defaultFeedbackScope;
  }

  async #handleCreateConversationClicked() {
    if (this._currentUserUid == null) return;

    const entityData = {
      job_id: this.filters.jobId,
      project_id: this.filters.projectId,
      entity_id: this.filters.entityId,
      entity_type: this.filters.entityType,
      company_id: this._currentUserActiveCompany
    };
    const newConversationRef = await createConversation(entityData, this._currentUserUid, !this.allowPrivateFeedback);
    this.conversationRefPath = newConversationRef?.path;
  }

  async setConversationIdFromSubEntity(entityData) {
    // only allow private users to leave feedback on sub entities
    if (this._currentUserUid == null || !this.allowPrivateFeedback) return;

    /** @type {CollectionReference|Query} */
    let query = firebase.firestore().collection(`companies/${entityData.company_id}/feedback`);
    for (const [field, value] of Object.entries(entityData)) {
      query = query.where(field, '==', value);
    }

    const queryResult = await query.get();
    // If the results are empty, create a new conversation, returning its ref
    if (queryResult.empty) {
      // Create the new conversation id and set the ref
      const newConversationRef = await createConversation(entityData, this._currentUserUid, !this.allowPrivateFeedback);
      this.conversationRefPath = newConversationRef?.path;
    } else {
      // Return the first conversation
      this.conversationRefPath = queryResult.docs[0].ref.path;
    }
  }

  showList() {
    this.conversationRefPath = null;
    if (this.filters) {
      this.filters.subEntityId = '';
      this.filters.subEntityType = '';
    }
  }
}
customElements.define('feedback-element', FeedbackElement);
